import React, { useState, useEffect } from "react";
import { Filter, Sliders, Calendar } from "react-feather";
import { Link } from "react-router-dom";
import Select from "react-select";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Breadcrumbs from "../../core/breadcrumbs";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";
import { Zap } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

const ExpenseReport = () => {
  const { showToast } = useToast();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [expenseData, setExpenseData] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
  });

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  // Sort options
  const options = [
    { value: "sortByDate", label: "Sort by Date" },
    { value: "140923", label: "14 09 23" },
    { value: "110923", label: "11 09 23" },
  ];

  // Dynamic dropdown options
  const optionsCategory = [
    { value: "chooseCategory", label: "Choose Category" },
    ...new Set(
      expenseData.map((item) => ({
        value: item.category || "Unknown",
        label: item.category || "Unknown",
      }))
    ),
  ];

  // Date range picker settings
  const initialSettings = {
    endDate: new Date(),
    ranges: {
      "Last 30 Days": [
        new Date(new Date().setDate(new Date().getDate() - 30)),
        new Date(),
      ],
      "Last 7 Days": [
        new Date(new Date().setDate(new Date().getDate() - 7)),
        new Date(),
      ],
      "Last Month": [
        new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
        new Date(new Date().getFullYear(), new Date().getMonth(), 0),
      ],
      "This Month": [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date(),
      ],
      Today: [new Date(), new Date()],
      Yesterday: [
        new Date(new Date().setDate(new Date().getDate() - 1)),
        new Date(new Date().setDate(new Date().getDate() - 1)),
      ],
    },
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    timePicker: false,
  };

  // Handle date range change
  const handleDateRangeChange = (event, picker) => {
    setDateRange({
      startDate: picker.startDate.toDate(),
      endDate: picker.endDate.toDate(),
    });
  };

  // Fetch expense report
  const fetchExpenseReport = async () => {
    try {
      const response = await apiClient.post(`/generateExpenseReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setExpenseData(response.data);
      } else {
        setExpenseData([]);
        showToast("No expense data found", "warning");
      }
    } catch (error) {
      console.error("Failed to fetch expense report:", error);
      showToast("Failed to fetch expense report", "error");
    }
  };

  useEffect(() => {
    fetchExpenseReport();
  }, []);

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generateExpenseReport`, {});
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "expense-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Expense report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      showToast("Failed to export expense report to Excel", "error");
    }
  };

  // Handle PDF export
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Header
      doc.setFontSize(20);
      doc.text("Expense Report", 14, 20);
      doc.setFontSize(12);
      doc.text(
        `Date Range: ${dateRange.startDate.toLocaleDateString("en-GB")} - ${dateRange.endDate.toLocaleDateString("en-GB")}`,
        14,
        30
      );
      doc.text("Generated on: July 30, 2025, 11:52 AM PKT", 14, 40);

      // Table
      doc.autoTable({
        startY: 50,
        head: [["Date", "Expense Category", "Expense Name", "Amount"]],
        body: expenseData.map((expense) => [
          formatDate(expense.date),
          expense.category || "N/A",
          expense.expense_name || "N/A",
          expense.amount ? `$${parseFloat(expense.amount).toFixed(2)}` : "N/A",
        ]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [100, 100, 100], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 50 },
      });

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: "right" });
      }

      // Save PDF
      doc.save("expense-report.pdf");
      showToast("Expense report exported to PDF successfully", "success");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      showToast("Failed to export expense report to PDF", "error");
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Render expense rows
  const renderExpenseRows = () => {
    return expenseData.map((expense, index) => (
      <tr key={index}>
        <td>
          <label className="checkboxs">
            <input type="checkbox" />
            <span className="checkmarks" />
          </label>
        </td>
        <td>{formatDate(expense.date)}</td>
        <td>{expense.category || "N/A"}</td>
        <td>{expense.expense_name || "N/A"}</td>
        <td>{expense.amount ? `$${parseFloat(expense.amount).toFixed(2)}` : "N/A"}</td>
      </tr>
    ));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Expenses Report"
          subtitle="Manage Your Expenses Report"
          onExcelClick={handleExportExcel}
          onPDFClick={handleExportPDF}
        />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                  />
                  <Link to="#" className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
              <div className="search-path">
                <Link
                  className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                  id="filter_search"
                  onClick={toggleFilterVisibility}
                >
                  <Filter className="filter-icon" />
                  <span>
                    <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                  </span>
                </Link>
              </div>
              <div className="form-sort stylewidth">
                <Sliders className="info-img" />
                <Select
                  className="select"
                  options={options}
                  placeholder="Sort by Date"
                />
              </div>
            </div>
            <div
              className={`card ${isFilterVisible ? "visible" : ""}`}
              id="filter_inputs"
              style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-3 col-sm-6 col-12">
                    <div className="input-blocks">
                      <Zap className="info-img" />
                      <Select
                        className="select"
                        options={optionsCategory}
                        placeholder="Choose Category"
                      />
                    </div>
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12">
                    <div className="input-blocks">
                      <div className="position-relative daterange-wraper">
                        <Calendar className="feather-14 info-img" />
                        <DateRangePicker
                          initialSettings={initialSettings}
                          onCallback={handleDateRangeChange}
                        >
                          <input
                            className="form-control col-4 input-range"
                            type="text"
                            style={{ border: "none" }}
                          />
                        </DateRangePicker>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                    <div className="input-blocks">
                      <Link to="#" className="btn btn-filters ms-auto">
                        <i data-feather="search" className="feather-search" /> Search
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table datanew">
                <thead>
                  <tr>
                    <th className="no-sort">
                      <label className="checkboxs">
                        <input type="checkbox" id="select-all" />
                        <span className="checkmarks" />
                      </label>
                    </th>
                    <th>Date</th>
                    <th>Expense Category</th>
                    <th>Expense Name</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody className="Expense-list">
                  {expenseData.length > 0 ? (
                    renderExpenseRows()
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;